import { Link } from "react-router-dom";
import { MAIN_SITE_LINKS } from "@/lib/domainConfig";
import useExternalAnalytics from "@/hooks/useExternalAnalytics";

/**
 * Item #5 — Persona self-identification strip.
 *
 * Surfaces every audience the competitive landscape report names:
 * actors, parents, coaches, studio owners, talent managers, talent
 * agents, casting pros, writers, HMU, crew, producers/service
 * providers. Each chip either anchors a platform card on this page
 * (`platform:<key>`) or links out to the marketing persona page on
 * castready.org for personas without a dedicated hub workspace.
 */
type Target = { kind: "platform"; key: string } | { kind: "external"; href: string };

const PERSONAS: Array<{ label: string; target: Target }> = [
  { label: "Actor",          target: { kind: "platform", key: "callback-zone" } },
  { label: "Parent",         target: { kind: "platform", key: "callback-zone" } },
  { label: "Coach",          target: { kind: "platform", key: "coach" } },
  { label: "Studio owner",   target: { kind: "platform", key: "coach" } },
  { label: "Talent manager", target: { kind: "platform", key: "coach" } },
  { label: "Talent agent",   target: { kind: "platform", key: "coach" } },
  { label: "Casting pro",    target: { kind: "platform", key: "castings" } },
  { label: "Partner / brand", target: { kind: "platform", key: "pro" } },
  { label: "Writer",         target: { kind: "external", href: MAIN_SITE_LINKS.home + "/for/screenwriters" } },
  { label: "HMU artist",     target: { kind: "external", href: MAIN_SITE_LINKS.home + "/for/hmu-artists" } },
  { label: "Crew / producer", target: { kind: "external", href: MAIN_SITE_LINKS.home + "/for/producers" } },
];

export default function PersonaRouter() {
  const { trackBoth } = useExternalAnalytics();

  const scrollToPlatform = (key: string) => {
    const el = document.getElementById("platform-" + key);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary/60");
      window.setTimeout(() => el.classList.remove("ring-2", "ring-primary/60"), 1400);
    }
  };

  return (
    <div className="mb-6">
      <p className="text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-2">
        I'm a…
      </p>
      <div className="flex flex-wrap justify-center gap-1.5">
        {PERSONAS.map((p) => {
          const handleClick = () => {
            trackBoth("hub_persona_chip_click", { persona: p.label });
            if (p.target.kind === "platform") scrollToPlatform(p.target.key);
          };
          if (p.target.kind === "external") {
            return (
              <a
                key={p.label}
                href={p.target.href}
                onClick={handleClick}
                className="px-3 py-1 rounded-full border border-border bg-card/60 backdrop-blur text-xs text-foreground hover:border-primary/50 hover:bg-primary/5 transition-colors"
              >
                {p.label}
              </a>
            );
          }
          return (
            <button
              key={p.label}
              type="button"
              onClick={handleClick}
              className="px-3 py-1 rounded-full border border-border bg-card/60 backdrop-blur text-xs text-foreground hover:border-primary/50 hover:bg-primary/5 transition-colors"
            >
              {p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}